import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    IconButton,
    Grid,
    Chip,
    Alert,
    Snackbar
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
    getMessageTemplates,
    createMessageTemplate,
    updateMessageTemplate,
    deleteMessageTemplate,
    toggleTemplateActive
} from '../api/messageTemplateApi';

const MessageTemplates = ({ affiliateId }) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const quillRef = useRef(null); // Lisa see
    const isUserChange = useRef(false); // Lisa see

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        subject: '',
        body: '',
        type: 'first_training',
        active: true
    });

    const templateTypes = [
        { value: 'first_training', label: 'First Training Registration' },
        { value: 'plan_expiry', label: 'Plan Expiry Reminder' },
        { value: 'training_followup', label: 'Training Follow-up (24h after class)' },
        { value: 'inactive_14_days', label: 'Inactive 21 Days (no training)' }
    ];

    // React-Quill configuration
    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            [{ 'align': [] }],
            ['link'],
            ['clean']
        ],
    };

    const quillFormats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'color', 'background',
        'list', 'bullet', 'indent',
        'align',
        'link'
    ];

    useEffect(() => {
        fetchTemplates();
    }, [affiliateId]);

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            const data = await getMessageTemplates(affiliateId);
            setTemplates(data);
        } catch (error) {
            showSnackbar('Error fetching templates', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleDialogOpen = (template = null) => {
        if (template) {
            setEditingTemplate(template);
            // DON'T clean when loading - let ReactQuill handle the HTML as-is
            setFormData({
                name: template.name,
                subject: template.subject,
                body: template.body, // Load as-is from database
                type: template.type,
                active: template.active
            });
        } else {
            setEditingTemplate(null);
            setFormData({
                name: '',
                subject: '',
                body: '',
                type: 'first_training',
                active: true
            });
        }
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setEditingTemplate(null);
        setFormData({
            name: '',
            subject: '',
            body: '',
            type: 'first_training',
            active: true
        });
    };

    // Clean ReactQuill HTML to prevent double line breaks
    const cleanQuillHTML = (html) => {
        if (!html) return '';
        

        
        let cleaned = html;
        
        // Remove whitespace between tags
        cleaned = cleaned.replace(/>\s*\n\s*</g, '><');
        
        // ReactQuill automatically adds spacing after headers and lists
        // Remove <p><br></p> immediately after these elements to prevent double spacing
        cleaned = cleaned
            // After any heading (h1-h6)
            .replace(/<\/(h[1-6])><p><br><\/p>/gi, '</$1>')
            // After ordered list
            .replace(/<\/ol><p><br><\/p>/gi, '</ol>')
            // After unordered list  
            .replace(/<\/ul><p><br><\/p>/gi, '</ul>')
            // Before any heading (h1-h6)
            .replace(/<p><br><\/p><(h[1-6])>/gi, '<$1>');
        
        // Remove consecutive empty paragraphs (more than one in a row)
        // Keep maximum of 1 empty paragraph (which represents 1 empty line)
        cleaned = cleaned.replace(/(<p><br><\/p>){2,}/gi, '<p><br></p>');
        
        // Remove leading/trailing empty paragraphs
        cleaned = cleaned.replace(/^(<p><br><\/p>)+/gi, '');
        cleaned = cleaned.replace(/(<p><br><\/p>)+$/gi, '');
        

        
        return cleaned.trim();
    };

    // Uuendatud onChange handler
    const handleQuillChange = (content, delta, source, editor) => {
        // Ainult uuenda, kui muudatus tuli kasutajalt (user), mitte API-st (api/silent)
        if (source === 'user') {
            setFormData({ ...formData, body: content });
        }
    };

    const handleSubmit = async () => {
        try {
            // Clean the body HTML before saving
            const cleanedBody = cleanQuillHTML(formData.body);
            
            const dataToSave = {
                ...formData,
                body: cleanedBody,
                affiliateId: parseInt(affiliateId)
            };

            if (editingTemplate) {
                await updateMessageTemplate(editingTemplate.id, dataToSave);
                showSnackbar('Template updated successfully');
            } else {
                await createMessageTemplate(dataToSave);
                showSnackbar('Template created successfully');
            }
            fetchTemplates();
            handleDialogClose();
        } catch (error) {
            showSnackbar('Error saving template', 'error');
        }
    };

    const handleDelete = async (templateId) => {
        if (window.confirm('Are you sure you want to delete this template?')) {
            try {
                await deleteMessageTemplate(templateId);
                showSnackbar('Template deleted successfully');
                fetchTemplates();
            } catch (error) {
                showSnackbar('Error deleting template', 'error');
            }
        }
    };

    const handleToggleActive = async (templateId) => {
        try {
            await toggleTemplateActive(templateId);
            showSnackbar('Template status updated successfully');
            fetchTemplates();
        } catch (error) {
            showSnackbar('Error updating template status', 'error');
        }
    };

    const getTypeLabel = (type) => {
        const typeObj = templateTypes.find(t => t.value === type);
        return typeObj ? typeObj.label : type;
    };

    // Function to strip HTML tags for display
    const stripHtml = (html) => {
        const tmp = document.createElement('DIV');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">Message Templates</Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleDialogOpen()}
                    sx={{ backgroundColor: '#d4af37', '&:hover': { backgroundColor: '#b8941f' } }}
                >
                    Add Template
                </Button>
            </Box>

            {loading ? (
                <Typography>Loading templates...</Typography>
            ) : (
                <Grid container spacing={3}>
                    {templates.map((template) => (
                        <Grid item xs={12} md={6} key={template.id}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Typography variant="h6" component="h2">
                                            {template.name}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDialogOpen(template)}
                                                color="primary"
                                            >
                                                <Edit />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDelete(template.id)}
                                                color="error"
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Box>
                                    </Box>

                                    <Box sx={{ mb: 2 }}>
                                        <Chip
                                            label={getTypeLabel(template.type)}
                                            color="primary"
                                            size="small"
                                            sx={{ mb: 1 }}
                                        />
                                        <Chip
                                            label={template.active ? 'Active' : 'Inactive'}
                                            color={template.active ? 'success' : 'default'}
                                            size="small"
                                            sx={{ ml: 1, mb: 1 }}
                                        />
                                    </Box>

                                    <Typography variant="subtitle2" color="text.secondary">
                                        Subject:
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        {template.subject}
                                    </Typography>

                                    <Typography variant="subtitle2" color="text.secondary">
                                        Body:
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        {stripHtml(template.body).length > 100
                                            ? `${stripHtml(template.body).substring(0, 100)}...`
                                            : stripHtml(template.body)}
                                    </Typography>

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={template.active}
                                                onChange={() => handleToggleActive(template.id)}
                                            />
                                        }
                                        label="Active"
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Template Dialog */}
            <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    {editingTemplate ? 'Edit Template' : 'Create New Template'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Template Name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            fullWidth
                            required
                        />

                        <FormControl fullWidth required>
                            <InputLabel>Template Type</InputLabel>
                            <Select
                                value={formData.type}
                                label="Template Type"
                                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                            >
                                {templateTypes.map((type) => (
                                    <MenuItem key={type.value} value={type.value}>
                                        {type.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Email Subject"
                            value={formData.subject}
                            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                            fullWidth
                            required
                        />

                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Email Body *
                            </Typography>
                            <ReactQuill
                                ref={quillRef}
                                theme="snow"
                                value={formData.body}
                                onChange={handleQuillChange}
                                modules={quillModules}
                                formats={quillFormats}
                                style={{
                                    height: '300px',
                                    marginBottom: '50px'
                                }}
                                placeholder="Enter your email template content here..."
                            />
                        </Box>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.active}
                                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                                />
                            }
                            label="Active"
                        />
                    </Box>

                    {formData.type === 'first_training' && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            This template will be automatically sent when a user registers for their first training at your gym.
                        </Alert>
                    )}

                    {formData.type === 'plan_expiry' && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            This template will be automatically sent 5 days before a user's plan expires.
                        </Alert>
                    )}

                    {formData.type === 'training_followup' && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            This template will be automatically sent 24 hours after a user's first training class has ended.
                        </Alert>
                    )}

                    {(formData.type === 'inactive_14_days' || formData.type === 'inactive_21_days') && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            This template will be automatically sent at night when a user has not trained for exactly 21 days (date-based check, check-in is not used).
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        sx={{ backgroundColor: '#d4af37', '&:hover': { backgroundColor: '#b8941f' } }}
                    >
                        {editingTemplate ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default MessageTemplates;